'use client';

import { AISparkle } from '@/components/shared/AIComponents';

const capabilities = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Fink AI Insights',
    description: 'Get instant AI-powered health reports for every client. Fink analyzes filings, demands, and notices to surface what needs attention.',
    examples: [
      'Client health scores',
      'Priority recommendations',
      'Portfolio-wide analysis'
    ],
    gradient: 'from-violet-500 to-indigo-600',
    bgGradient: 'from-violet-50 to-indigo-50',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    title: 'Ask Fink Anything',
    description: 'Chat with Fink, our built-in AI assistant. Ask questions about your tax data in plain English and get instant answers.',
    examples: [
      '"Which clients have overdue notices?"',
      '"Show me high-value demands"',
      '"What\'s due this week?"'
    ],
    gradient: 'from-[#0D60E6] to-[#0D52C2]',
    bgGradient: 'from-blue-50 to-indigo-50',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    title: 'Smart Document Analysis',
    description: 'Upload tax notices to Finkr and let AI extract key information instantly. Identifies risks, deadlines, and required actions.',
    examples: [
      'Auto-detect notice sections',
      'Extract demand amounts',
      'Identify response deadlines'
    ],
    gradient: 'from-cyan-500 to-blue-600',
    bgGradient: 'from-cyan-50 to-blue-50',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'AI-Generated Responses',
    description: 'Finkr generates professionally drafted responses to tax notices in seconds. Compliant, accurate, and ready to review.',
    examples: [
      'Draft compliance letters',
      'Appeal objections',
      'Submission responses'
    ],
    gradient: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-50 to-teal-50',
  },
];

export default function AICapabilitiesGrid() {
  return (
    <section className="py-12 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-blue-50 border border-blue-200 rounded-full mb-4">
            <span className="w-2 h-2 rounded-full bg-[#0D60E6]"></span>
            <span className="text-xs lg:text-sm font-semibold text-[#0D52C2]">AI Capabilities</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 lg:mb-4 px-4">
            Finkr Works Smarter, So You Don&apos;t Have To
          </h2>
          <p className="text-sm lg:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Four powerful AI capabilities built into Finkr that transform how you manage tax compliance
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {capabilities.map((cap, index) => (
            <div
              key={index}
              className="group relative p-4 lg:p-6 rounded-2xl border border-gray-100 bg-white hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${cap.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

              {/* Content */}
              <div className="relative z-10">
                {/* Icon */}
                <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br ${cap.gradient} flex items-center justify-center text-white mb-4 lg:mb-6 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                  {cap.icon}
                </div>

                {/* AI Sparkle */}
                <AISparkle className="absolute top-4 right-4 lg:top-6 lg:right-6 w-4 h-4 lg:w-5 lg:h-5 text-[#0D60E6] opacity-0 group-hover:opacity-100 transition-opacity" />

                <h3 className="text-lg lg:text-xl font-bold text-gray-900 mb-2 lg:mb-3">
                  {cap.title}
                </h3>

                <p className="text-sm lg:text-base text-gray-600 mb-4 lg:mb-6">
                  {cap.description}
                </p>

                {/* Examples */}
                <div className="space-y-1.5 lg:space-y-2">
                  {cap.examples.map((example, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs lg:text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${cap.gradient} flex-shrink-0`} />
                      <span className="text-gray-500">{example}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
